/**
 * `<CombatStage>`'s module-level constants — zoom bounds, the authoring-preview palette, and the
 * ping/cursor gesture budgets. Split out of `../CombatStage` verbatim; the values and their reasons
 * are unchanged.
 */

/** How many recent float disclosures `__combatBoardDebug.getFloatLog()` keeps. Diagnostic only. */
export const FLOAT_LOG_CAP = 32;

// The world-px distance (squared, to dodge a `sqrt`) within which two consecutive raw polyline
// clicks count as a genuine same-spot "double-click to finish" gesture, vs. two rapid but
// DIFFERENT-location vertex placements that merely landed inside the browser's fixed dblclick
// timing window (see `polylineRawClicksRef`'s doc comment in `CombatStage` — the native `dblclick`
// event has no distance check of its own). Generous enough to tolerate a real double-click's
// small hand jitter, well under a cell (`CELL` = 40) so it never reads as "the next vertex."
export const POLYLINE_DBLCLICK_FINISH_DIST = 16;
export const POLYLINE_DBLCLICK_FINISH_DIST_SQ = POLYLINE_DBLCLICK_FINISH_DIST * POLYLINE_DBLCLICK_FINISH_DIST;

/** Zoom bounds + per-wheel-notch factor. */
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;
export const ZOOM_STEP = 1.08;

// ── Holo cell treatments / walls / rooms ──────────────────────────────────────────────────────────
// The real board render (board bg/lattice/bezel/ruler, terrain treatments, committed walls, room
// floors) moved to the Pixi renderer (`pixiCombatBoard.ts`) —
// its own palette constants live there now. What's left here is the **authoring-preview** palette:
// still needed because this component resolves the preview's direction/color (via
// `strokeDirection` / `wallRunDirection`, unchanged pure helpers) before handing a plain spec to
// `pixiCombatBoard.ts`'s `setAuthoringPreview` — see the `authoringPreview` effect below.
// Committed walls take this cyan holo accent; the AUTHORING add-preview keeps its
// own WARM cue (gold), the deliberate counterpart to the removal red: add = gold ghost, remove = red
// ghost, read apart at a glance.
export const WALL_STROKE = "#5fe3ff";
export const EDGE_WALL = "#c9a23e";
export const PREVIEW_OPACITY = 0.5;
export const PREVIEW_ADD_STROKE = EDGE_WALL;
export const PREVIEW_ADD_FILL = "rgba(95, 227, 255, 0.34)"; // add-ghost tint until the tile swatch has painted
export const REMOVE_STROKE = "#c06a6a";
export const REMOVE_FILL = "rgba(192, 106, 106, 0.20)";

// Map ping (this feature) — the attention-directing pulse a long-press drops, broadcast to every
// screen. `PING_HOLD_MS` is how long the left button must be held (still) to fire; a pointer travel
// past `PING_MOVE_TOLERANCE` screen px cancels the hold (it was a drag, not a hold). The burst's own
// render (tint, lifetime) is `pixiCombatBoard.ts`'s `addPing` now (§3 row 13).
export const PING_HOLD_MS = 450;
export const PING_MOVE_TOLERANCE = 6;

// Live cursors (this feature) — each participant's pointer streamed to everyone else. `CURSOR_THROTTLE_MS`
// caps how often we emit our own move (the stream is high-frequency; ~50ms is smooth without flooding
// the socket). The received-cursor render (lerp/fade/reticle) is `pixiCombatBoard.ts`'s `setCursors`
// now (§3 row 15).
export const CURSOR_THROTTLE_MS = 50;

/** A right-button press that moves less than this is a click (it disarms the board), not the start of a pan. */
export const RIGHT_CLICK_SLOP_PX = 4;
